import { useMemo, useState } from "react";

/**
 * DeployedTeamsPanel
 * ------------------
 * Left-side dispatch control panel for the NI & Migration Map Views.
 * Replaces the old barangay/statistics accordion cards.
 *
 * Groups the currently-loaded map records by their assigned team and renders
 * one compact, collapsible card per deployed team. Collapsed cards show the
 * team number, composition and P/C/R/X status counts. Expanding a card reveals
 * the individual job orders, each of which is clickable.
 *
 * This component is intentionally page-agnostic: NI and Migration records use
 * slightly different field names, so every accessor below falls back across
 * both shapes. No data is fetched here — it derives everything from the
 * `records` prop (already filtered by date / municipality / team / search on
 * the host page).
 *
 * Props:
 *  - records:          array of map records (NI or Migration `mapRecords`)
 *  - teamColorMap:     { [team_number]: cssColor }
 *  - statusColors:     STATUS_COLORS map from the host page (for badge colors)
 *  - isToday:          when false, the Rescheduled bucket is hidden (map parity)
 *  - historicalMode:   when true (host passes this for past dates), the R column
 *                      is shown regardless of isToday and Rescheduled records that
 *                      were auto-rolled by the nightly job ("Resched by System")
 *                      are counted back into the Pending (P) bucket. This lets a
 *                      past-date card reconstruct the P/R it showed that day: the
 *                      live records only survive as C and X once the date is past,
 *                      while the rolled-over Pending / manually-Rescheduled JOs
 *                      live on as dated history snapshots.
 *  - selectedRecordId: id of the currently-selected record (for highlight)
 *  - onJobOrderClick:  (record) => void — host pans the map + opens Subscriber
 *  - title:            optional panel heading (default "Deployed Teams")
 *  - showOtherCompany: when false (default), the host passes only the user's
 *                      own-company records; this drives the checkbox state
 *  - onToggleOtherCompany: (checked) => void — toggles the "include other
 *                      company" checkbox (omit to hide the checkbox entirely)
 */

// Map any raw status string to one of the four display buckets.
// Handles NI ("Active/Complete") and Migration ("Completed") plus spelling
// variants ("Canceled"). Used for counting/display only — never mutates data.
function statusBucket(raw) {
  const u = (raw || "").toLowerCase();
  if (u.includes("cancel")) return "cancelled";
  if (u.includes("resched")) return "rescheduled";
  if (u.includes("complete")) return "completed"; // matches "active/complete" too
  return "pending";
}

const BUCKET_LABEL = {
  pending: "Pending",
  completed: "Completed",
  rescheduled: "Rescheduled",
  cancelled: "Cancelled",
};

// Order job orders within an expanded team: pending → completed → resched → cancelled
const BUCKET_ORDER = { pending: 0, completed: 1, rescheduled: 2, cancelled: 3 };

// --- record field accessors (work for both NI and Migration shapes) ---
const recRef = (r) => r.jo_number || r.account_number || r.oldacctno || "—";
const recName = (r) => r.name || r.customer_full_name || "";
const recComposition = (r) => r.personnel_names || r.personnel || "";
const recAddress = (r) => r.complete_address || r.address || "";
const recLocation = (r) =>
  [r.barangay, (r.municipality || "").replace(/^CITY OF\s+/i, "")]
    .filter(Boolean)
    .join(", ");
const recHasCoords = (r) =>
  r.latitude != null &&
  r.longitude != null &&
  !isNaN(r.latitude) &&
  !isNaN(r.longitude);
const hasTeam = (tn) => tn && String(tn).trim() && String(tn).trim() !== "-";

// A Rescheduled record produced by the nightly auto-rollover represents a JO that
// was still Pending at the end of its scheduled day (never actioned, so the system
// rolled it forward). On past dates the live Pending records are gone, so in
// historical mode we count these back into Pending to reconstruct that day's P.
// Manually-rescheduled JOs (a real user touched them) stay in the Rescheduled (R)
// bucket. The authoritative signal is `is_system_resched` from the map-records
// endpoint (rescheduled_by IS NULL). The remark/reason strings are only a fallback
// for older payloads that predate that flag. Page-agnostic: NI stamps the remark on
// `jo_status_remarks`, Migration on `remarks`; reason strings differ per module.
const SYSTEM_RESCHED_REASONS = new Set(["Resched by System", "System auto-reschedule"]);
const isSystemResched = (r) =>
  r.is_system_resched === true ||
  r.jo_status_remarks === "Resched by System" ||
  r.remarks === "Resched by System" ||
  (Array.isArray(r.resched_reasons) &&
    r.resched_reasons.some((x) => SYSTEM_RESCHED_REASONS.has(x)));

export default function DeployedTeamsPanel({
  records,
  teamColorMap = {},
  statusColors = {},
  isToday = true,
  historicalMode = false,
  selectedRecordId = null,
  onJobOrderClick,
  onNameClick,
  title = "Deployed Teams",
  showOtherCompany = false,
  onToggleOtherCompany,
}) {
  const [expanded, setExpanded] = useState(() => new Set());

  const bucketColors = useMemo(
    () => ({
      pending: statusColors.Pending || "#eab308",
      completed: statusColors["Active/Complete"] || statusColors.Completed || "#22c55e",
      rescheduled: statusColors.Rescheduled || "#f97316",
      cancelled: statusColors.Cancelled || "#ef4444",
    }),
    [statusColors]
  );

  // Group records by team. Records without a team are excluded — this mirrors
  // the existing "Teams Today" legend, which only lists dispatched teams.
  const teams = useMemo(() => {
    const map = new Map();
    for (const r of records || []) {
      if (!hasTeam(r.team_number)) continue;
      const tn = String(r.team_number).trim();
      let t = map.get(tn);
      if (!t) {
        t = {
          team_number: tn,
          composition: "",
          counts: { pending: 0, completed: 0, rescheduled: 0, cancelled: 0 },
          jobOrders: [],
        };
        map.set(tn, t);
      }
      let bucket = statusBucket(r.status || r.jo_status);
      // On past dates, fold auto-rolled ("Resched by System") records back into
      // Pending so the card shows the P that was outstanding that day.
      if (historicalMode && bucket === "rescheduled" && isSystemResched(r)) {
        bucket = "pending";
      }
      t.counts[bucket] += 1;
      if (!t.composition) t.composition = recComposition(r);
      t.jobOrders.push({ record: r, bucket });
    }
    const list = [...map.values()];
    for (const t of list) {
      t.total = t.jobOrders.length;
      t.jobOrders.sort((a, b) => {
        const d = BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket];
        return d !== 0 ? d : String(recRef(a.record)).localeCompare(String(recRef(b.record)));
      });
    }
    list.sort((a, b) => a.team_number.localeCompare(b.team_number, undefined, { numeric: true }));
    return list;
  }, [records, historicalMode]);

  const toggle = (tn) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(tn)) next.delete(tn);
      else next.add(tn);
      return next;
    });

  return (
    <div className="bg-white rounded-lg shadow flex flex-col min-h-0">
      {/* Header row: title + count together, then "other company" toggle */}
      <div className="flex items-center gap-1.5 px-3 py-2.5 flex-shrink-0">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {title}
        </span>
        <span className="text-[10px] font-bold text-gray-500 tabular-nums bg-gray-100 rounded-full px-1.5 min-w-[18px] text-center leading-tight">
          {teams.length}
        </span>
        {onToggleOtherCompany && (
          <label className="flex items-center gap-1 cursor-pointer ml-auto" title="Show teams from other companies">
            <input
              type="checkbox"
              checked={showOtherCompany}
              onChange={(e) => onToggleOtherCompany(e.target.checked)}
              className="w-3 h-3 rounded"
            />
            <span className="text-[9px] text-gray-500 whitespace-nowrap">Other co.</span>
          </label>
        )}
      </div>

      <div className="px-2 pb-2 space-y-1.5 overflow-y-auto">
        {teams.length === 0 ? (
          <p className="text-xs text-gray-400 px-1 py-2">No deployed teams.</p>
        ) : (
          teams.map((t) => {
            const color = teamColorMap[t.team_number] || "#6b7280";
            const open = expanded.has(t.team_number);
            return (
              <div
                key={t.team_number}
                className="rounded-md border border-gray-200 bg-white overflow-hidden"
              >
                {/* Collapsed header (always visible) */}
                <button
                  onClick={() => toggle(t.team_number)}
                  className="w-full text-left px-2 py-1.5 hover:bg-gray-50 transition-colors"
                >
                  {/* Top row: team number + status counts on the same line */}
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-[11px] font-bold text-gray-800 truncate min-w-0">
                      {t.team_number}
                    </span>
                    <span
                      className="text-[9px] font-bold text-gray-500 tabular-nums bg-gray-100 rounded-full px-1.5 leading-tight flex-shrink-0"
                      title="Total job orders assigned today"
                    >
                      {t.total}
                    </span>
                    <span className="flex gap-1 text-[10px] tabular-nums ml-auto flex-shrink-0">
                      <span style={{ color: bucketColors.pending }} className="font-semibold">
                        P:{t.counts.pending}
                      </span>
                      <span style={{ color: bucketColors.completed }} className="font-semibold">
                        C:{t.counts.completed}
                      </span>
                      {(isToday || historicalMode) && (
                        <span style={{ color: bucketColors.rescheduled }} className="font-semibold">
                          R:{t.counts.rescheduled}
                        </span>
                      )}
                      <span style={{ color: bucketColors.cancelled }} className="font-semibold">
                        X:{t.counts.cancelled}
                      </span>
                    </span>
                    <svg
                      className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {t.composition && (
                    <div className="text-[10px] text-gray-500 truncate mt-0.5 pl-4">
                      {t.composition}
                    </div>
                  )}
                </button>

                {/* Expanded job orders */}
                {open && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-1.5 py-1.5 space-y-1">
                    {t.jobOrders.map(({ record: r, bucket }) => {
                      const isSelected = selectedRecordId != null && r.id === selectedRecordId;
                      const badgeColor = bucketColors[bucket];
                      return (
                        <div
                          key={r.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => onJobOrderClick?.(r)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onJobOrderClick?.(r);
                            }
                          }}
                          title={recHasCoords(r) ? "Locate on map" : "No saved coordinates"}
                          className={`w-full text-left rounded px-2 py-1 bg-white border-l-[3px] transition-colors cursor-pointer ${
                            isSelected
                              ? "ring-1 ring-blue-400 bg-blue-50"
                              : "hover:bg-gray-50"
                          }`}
                          style={{ borderLeftColor: badgeColor }}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10.5px] font-semibold text-gray-800 truncate flex-1">
                              {recRef(r)}
                            </span>
                            <span
                              className="text-[8.5px] font-bold uppercase tracking-wide px-1 py-[1px] rounded text-white flex-shrink-0"
                              style={{ backgroundColor: badgeColor }}
                            >
                              {BUCKET_LABEL[bucket]}
                            </span>
                          </div>
                          {recName(r) && (
                            onNameClick ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onNameClick(r); }}
                                title="Locate subscriber by address"
                                className="block max-w-full truncate text-left text-[10px] font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {recName(r)}
                              </button>
                            ) : (
                              <div className="text-[10px] text-gray-700 truncate">{recName(r)}</div>
                            )
                          )}
                          {recLocation(r) && (
                            <div className="text-[9.5px] text-gray-500 truncate">{recLocation(r)}</div>
                          )}
                          {recAddress(r) && (
                            <div className="text-[9.5px] text-gray-400 truncate">{recAddress(r)}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
